import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type EventLog } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate, cn } from '@/lib/utils';
import { Download, Search, Activity, RefreshCw, Calendar } from 'lucide-react';

export function Events() {
  const [eventFilter, setEventFilter] = useState<string | undefined>();
  const [customerId, setCustomerId] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['events', { event: eventFilter, customerId: customerId || undefined }],
    queryFn: () => adminApi.getEvents({ 
      event: eventFilter, 
      customerId: customerId || undefined,
      limit: 100 
    }),
  });

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Events</h1>
          <p className="text-sm text-muted-foreground mt-0.5">System activity log</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => refetch()} 
            className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all"
          >
            <RefreshCw className={cn("h-5 w-5 text-muted-foreground", isLoading && "animate-spin")} />
          </button>
          <button 
            onClick={() => adminApi.exportEvents()} 
            className="p-3 rounded-xl bg-secondary/50 active:scale-95 active:bg-secondary transition-all hidden sm:block"
          >
            <Download className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search by customer ID..."
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          className="pl-12 h-12 rounded-xl text-base"
        />
      </div>

      {/* Filter Pills */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide">
        {[
          { value: undefined, label: 'All' },
          { value: 'subscription', label: 'Subscriptions' },
          { value: 'instance', label: 'Instances' },
          { value: 'server', label: 'Servers' },
          { value: 'payment', label: 'Payments' },
        ].map((filter) => (
          <button
            key={filter.label}
            onClick={() => setEventFilter(filter.value)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all active:scale-95",
              eventFilter === filter.value
                ? "bg-foreground text-background"
                : "bg-secondary text-muted-foreground"
            )}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[50vh]">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-muted border-t-foreground" />
        </div>
      ) : data?.events.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20">
          <Activity className="h-14 w-14 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No events found</p>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block rounded-xl border border-border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent border-border">
                  <TableHead className="text-xs uppercase tracking-wider">Event</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Customer</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Instance</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Data</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.events.map((event: EventLog) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <EventBadge event={event.event} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {event.customerId?.slice(-8) || '—'}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {event.instanceId?.slice(-8) || '—'}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <span className="text-xs text-muted-foreground truncate block">
                        {event.data ? JSON.stringify(event.data).slice(0, 60) : '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(event.createdAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-2">
            {data?.events.map((event: EventLog) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </>
      )}

      {data && (
        <p className="text-xs text-muted-foreground text-center">
          {data.events.length} of {data.total} events
        </p>
      )}
    </div>
  );
}

function EventBadge({ event }: { event: string }) {
  let variant: 'success' | 'warning' | 'destructive' | 'secondary' = 'secondary';
  
  if (event.includes('error') || event.includes('failed')) variant = 'destructive';
  else if (event.includes('started') || event.includes('activated') || event.includes('created')) variant = 'success';
  else if (event.includes('stopped') || event.includes('cancelled')) variant = 'warning';

  const formattedEvent = event
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return <Badge variant={variant}>{formattedEvent}</Badge>;
}

function EventCard({ event }: { event: EventLog }) {
  const getEventStyle = (eventName: string) => {
    if (eventName.includes('error') || eventName.includes('failed')) {
      return { dot: 'status-dot-error', bg: 'bg-status-error/10' };
    }
    if (eventName.includes('started') || eventName.includes('activated') || eventName.includes('created')) {
      return { dot: 'status-dot-running', bg: 'bg-status-success/10' };
    }
    if (eventName.includes('stopped') || eventName.includes('cancelled')) {
      return { dot: 'status-dot-pending', bg: 'bg-status-warning/10' };
    }
    return { dot: 'status-dot-stopped', bg: 'bg-secondary/30' };
  };

  const style = getEventStyle(event.event);
  const formattedEvent = event.event
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return (
    <div className={cn("mobile-card", style.bg)}>
      <div className="flex items-start gap-3">
        <div className={cn("status-dot mt-1.5 shrink-0", style.dot)} />
        <div className="flex-1 min-w-0">
          <p className="font-medium">{formattedEvent}</p>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            {event.customerId && (
              <span className="font-mono">{event.customerId.slice(-8)}</span>
            )}
            {event.instanceId && (
              <span className="font-mono">{event.instanceId.slice(-8)}</span>
            )}
          </div>
          {event.data && (
            <p className="text-xs text-muted-foreground mt-2 truncate">
              {JSON.stringify(event.data).slice(0, 60)}...
            </p>
          )}
        </div>
        <span className="text-xs text-muted-foreground shrink-0">
          {formatDate(event.createdAt)}
        </span>
      </div>
    </div>
  );
}
